import { ActionCreatorWithPayload, createAsyncThunk, createSlice, PayloadAction, ThunkDispatch } from "@reduxjs/toolkit";
import axios, { AxiosResponse } from "axios";
import { Card, CardJsonV2, defaultCard } from "./models/Card";
import { CardVariant, CardVariantName, VARIANT_SORT_VALUES, VariantJsonV2 } from "./models/CardVariant";
import { DbProductOverview } from "./models/Product";
import { RootState } from "./store";
import { getCardCollection } from "./repository/collectionRepository";
import { FormatRulesDictionary } from "./models/TournamentFormat";
import { ErrataSerialDictionary } from "./models/Errata";
import { TcgPlayerCardDetailsDictionary } from "./models/TcgPlayer";
import { FAQ } from "./models/FAQ";
import { FIREBASE_STORAGE_URL } from "./app-settings";
import { ComboBoxOption } from "./models/Input";
import { PriceDictionary } from "./models/Price";
import { getSiteCost } from "./repository/siteCostRepository";
import { SiteCost } from "./models/SiteCost";
import { OpusString, opusStringToId } from "./models/Opus";
import { getArchetypeNames } from "./repository/archetypeRepository";
import { ArchetypeName } from "./models/Archetype";
import { CardFilterCommands } from "./functions/filter/CardFilterCommands";
import { SortCommands } from "./functions/sort/SortCommands";
import { CARD_RARITIES, CardRarity } from "./models/CardRarity";

export const appSlice = createSlice({
    name: 'app',
    initialState: getInitialState(),
    reducers: {
        setCollection: (state, action: PayloadAction<Record<string, number>>) => {
            state.collection = action.payload;
        },
        setIsCollectionLoading: (state, action: PayloadAction<boolean>) => {
            state.isCollectionLoading = action.payload;
        },
        setCollectionDownloadProgress: (state, action: PayloadAction<number>) => {
            state.collectionDownloadProgress = action.payload * 100;
        },

        // CATEGORIES
        setIsCategoriesLoading: (state, action: PayloadAction<boolean>) => {
            state.isCategoriesLoading = action.payload;
        },
        setCategoryProgress: (state, action: PayloadAction<number>) => {
            state.categoryProgress = action.payload;
        },
        setCategoriesComplete: (state, action: PayloadAction<boolean>) => {
            state.categoriesComplete = action.payload;
        },

        // JOBS
        setIsJobsLoading: (state, action: PayloadAction<boolean>) => {
            state.isJobsLoading = action.payload;
        },
        setJobProgress: (state, action: PayloadAction<number>) => {
            state.jobProgress = action.payload;
        },
        setJobsComplete: (state, action: PayloadAction<boolean>) => {
            state.jobsComplete = action.payload;
        },

        // ARTISTS
        setIsArtistsLoading: (state, action: PayloadAction<boolean>) => {
            state.isArtistsLoading = action.payload;
        },
        setArtistProgress: (state, action: PayloadAction<number>) => {
            state.artistProgress = action.payload;
        },
        setArtistsComplete: (state, action: PayloadAction<boolean>) => {
            state.artistsComplete = action.payload;
        },

        // FORMAT
        setIsFormatRulesLoading: (state, action: PayloadAction<boolean>) => {
            state.isFormatRulesLoading = action.payload;
        },
        setFormatRulesDownloadProgress: (state, action: PayloadAction<number>) => {
            state.formatRulesDownloadProgress = action.payload * 100;
        },
        setFormatRules: (state, action: PayloadAction<FormatRulesDictionary>) => {
            state.formatRules = action.payload;
        },

        setSiteCost: (state, action: PayloadAction<SiteCost>) => {
            state.siteCost = action.payload;
        },

        setArchetypeNames: (state, action: PayloadAction<ArchetypeName[]>) => {
            state.archetypeNames = action.payload;
        },

        setIsErrataLoading: (state, action: PayloadAction<boolean>) => {
            state.isErrataLoading = action.payload;
        },
        setErrataDownloadProgress: (state, action: PayloadAction<number>) => {
            state.errataDownloadProgress = action.payload * 100;
        },
        setErrataSerialDictionary: (state, action: PayloadAction<ErrataSerialDictionary>) => {
            state.errataSerialDictionary = action.payload;
        },

        setIsFAQLoading: (state, action: PayloadAction<boolean>) => {
            state.isFAQLoading = action.payload;
        },
        setFAQDownloadProgress: (state, action: PayloadAction<number>) => {
            state.faqDownloadProgress = action.payload * 100;
        },
        setFAQ: (state, action: PayloadAction<FAQ[]>) => {
            state.faq = action.payload;
        },

        setIsTcgPlayerCardDetailsDictionaryLoading: (state, action: PayloadAction<boolean>) => {
            state.isTcgPlayerCardDetailsDictionaryLoading = action.payload;
        },
        setTcgPlayerCardDetailsDictionaryDownloadProgress: (state, action: PayloadAction<number>) => {
            state.tcgPlayerCardDetailsDictionaryDownloadProgress = action.payload * 100;
        },
        setTcgPlayerCardDetailsDictionary: (state, action: PayloadAction<TcgPlayerCardDetailsDictionary>) => {
            state.tcgPlayerCardDetailsDictionary = action.payload;
        },

        // CARDS
        setIsCardsLoading: (state, action: PayloadAction<boolean>) => {
            state.isRawCardsLoading = action.payload;
        },
        setCardsProgress: (state, action: PayloadAction<number>) => {
            state.rawCardsProgress = action.payload;
        },
        setCardsComplete: (state, action: PayloadAction<boolean>) => {
            state.cardsComplete = action.payload;
        },

        setIsProductsLoading: (state, action: PayloadAction<boolean>) => {
            state.isProductsLoading = action.payload;
        },
        setProducts: (state, action: PayloadAction<DbProductOverview[]>) => {
            const productIdDictionary: Record<number, DbProductOverview> = {};
            action.payload.forEach(product => {
                productIdDictionary[product.productId] = product;
            });

            state.products = action.payload;
            state.productIdDictionary = productIdDictionary;
            state.isProductsInitialized = true;
            state.isProductsLoading = false;
        },
        setIsProductDetailsLoading: (state, action: PayloadAction<boolean>) => {
            state.isProductDetailsLoading = action.payload;
        },
        setProductsProgress: (state, action: PayloadAction<number>) => {
            state.productsProgress = action.payload;
        },
        setProductsComplete: (state, action: PayloadAction<boolean>) => {
            state.productsComplete = action.payload;
        },

        setVariantPriceDownloadProgress: (state, action: PayloadAction<number>) => {
            state.variantPriceDownloadProgress = action.payload * 100;
        },
        setIsVariantPriceDictionaryLoading: (state, action: PayloadAction<boolean>) => {
            state.isVariantPriceDictionaryLoading = action.payload;
        },
        setVariantPrices: (state, action: PayloadAction<PriceDictionary>) => {
            state.variantPriceDictionary = action.payload;
            state.isVariantPriceDictionaryInitialized = true;
            state.isVariantPriceDictionaryLoading = false;
        },

        setRawVariantsLoading: (state, action: PayloadAction<boolean>) => {
            state.isRawCardVariantsLoading = action.payload;
        },
        setRawVariantsProgress: (state, action: PayloadAction<number>) => {
            state.variantsProgress = action.payload;
        },
        setVariantsComplete: (state, action: PayloadAction<boolean>) => {
            state.variantsComplete = action.payload;
        },

        setLoadingWaitTimerElapsed: (state, action: PayloadAction<boolean>) => {
            state.loadingWaitTimerElapsed = action.payload;
        },

        setRawCards: (state, action: PayloadAction<{
            rawCards: CardJsonV2[],
            rawCardVariants: VariantJsonV2[],
            categories: string[],
            jobs: string[],
            artists: string[]
        }>) => {
            const variants: CardVariant[] = [];
            const variantsById: Record<string, CardVariant> = {};

            const variantsBySerial: Record<string, CardVariant[]> = {};
            const variantsByImage: Record<string, CardVariant> = {};

            const playableVariantsById: Record<string, CardVariant> = {};
            const playableVariantsBySerial: Record<string, CardVariant[]> = {};
            const playableVariantsList: CardVariant[] = [];

            const primaryVariantsById: Record<string, CardVariant> = {};
            const primaryVariantsBySerial: Record<string, CardVariant> = {};
            const primaryVariantsList: CardVariant[] = [];

            const playablePrimaryVariantsById: Record<string, CardVariant> = {};
            const playablePrimaryVariantsBySerial: Record<string, CardVariant> = {};
            const playablePrimaryVariantsList: CardVariant[] = [];

            const uniqueVariantsById: Record<string, CardVariant> = {};
            const uniqueVariantsBySerial: Record<string, CardVariant[]> = {};
            const uniqueVariantsList: CardVariant[] = [];

            const playableUniqueVariantsById: Record<string, CardVariant> = {};
            const playableUniqueVariantsBySerial: Record<string, CardVariant[]> = {};
            const playableUniqueVariantsList: CardVariant[] = [];

            const promoVariantsById: Record<string, CardVariant> = {};
            const promoVariantsBySerial: Record<string, CardVariant[]> = {};
            const promoVariantsList: CardVariant[] = [];

            const fullArtVariantsById: Record<string, CardVariant> = {};
            const fullArtVariantsBySerial: Record<string, CardVariant[]> = {};
            const fullArtVariantsList: CardVariant[] = [];

            const cardsBySerialDictionary: Record<string, Card> = {};
            action.payload.rawCards.forEach(card => {
                const clonedCard: Card = {
                    categories: card.categories ?? [],
                    cost: card.cost ?? 0,
                    elements: card.elements ?? [],
                    imageId: card.imageId ?? "",
                    isExBurst: card.isExBurst ?? false,
                    isLB: card.isLB ?? false,
                    isMultiPlayable: card.isMultiPlayable ?? false,
                    isSpecial: card.isSpecial ?? false,
                    jobs: card.jobs ?? [],
                    name: card.name ?? "",
                    number: card.number ?? 0,
                    opus: card.opus ?? 0,
                    power: card.power ?? 0,
                    rarity: card.rarity ?? "",
                    serial: card.serial ?? "",
                    text: card.text ?? "",
                    type: card.type ?? "",
                    isSearcher: card.isSearcher ?? false,
                    normalizedEffectText: card.text
                        .replaceAll(/[.*,~'’]/g, "")
                        .replaceAll(/[\n]/g, " ")
                        .replaceAll("—", "-")
                        .toLocaleLowerCase()
                };

                cardsBySerialDictionary[card.serial] = clonedCard;
            });

            action.payload.rawCardVariants.sort((a, b) => {
                const MAX_SORT_VALUE = 999;
                const aSort = VARIANT_SORT_VALUES[a.variant as CardVariantName] ?? MAX_SORT_VALUE;
                const bSort = VARIANT_SORT_VALUES[b.variant as CardVariantName] ?? MAX_SORT_VALUE;
                return aSort - bSort;
            });

            action.payload.rawCardVariants.forEach(variant => {
                const card: Card = cardsBySerialDictionary[variant.serial] ?? cardsBySerialDictionary[variant?.altSerial ?? ""];
                if (!card) {
                    console.warn(`No card information found for variant ${variant.id}.`, variant);
                    return;
                }

                let altOpus: number | undefined = undefined;
                let altCardNumber: number | undefined = undefined;
                let altRarity: CardRarity | undefined = undefined;
                if (variant.altSerial) {
                    const altSerialParts = variant.altSerial.split("-");
                    const altOpusRaw = parseInt(altSerialParts[0]);
                    altOpus = isNaN(altOpusRaw) ? (opusStringToId[altSerialParts[0] as OpusString] ?? 0) : altOpusRaw;
                    altCardNumber = parseInt(altSerialParts[1]);

                    for (const rarity of CARD_RARITIES) {
                        if (variant.altSerial.endsWith(rarity)) {
                            altRarity = rarity;
                        }
                    }
                }

                const serialParts = variant.serial.split("-");
                const opusRaw = parseInt(serialParts[0]);
                const opus = isNaN(opusRaw) ? (opusStringToId[serialParts[0] as OpusString] ?? 0) : opusRaw;
                const cardNumber = parseInt(serialParts[1]);

                const convertedVariant: CardVariant = {
                    altSerial: variant.altSerial ?? "",
                    artists: variant.artists ?? [],
                    finish: variant.finish ?? "base",
                    id: variant.id ?? "",
                    imageId: variant.imageId ?? "",
                    isPrimaryVariant: variant.isPrimaryVariant ?? false,
                    isTempImage: variant.isTempImage ?? false,
                    octgnId: variant.octgnId ?? "",
                    products: variant.products ?? [],
                    serial: variant.serial ?? "",
                    tcgPlayerId: variant.tcgPlayerId ?? 0,
                    variant: variant.variant ?? "Non-Foil",
                    card: card,
                    opus: opus,
                    cardNumber: cardNumber,
                    altOpus: altOpus,
                    altCardNumber: altCardNumber,
                    altRarity: altRarity,
                    fullSerial: variant.altSerial ? `${variant.altSerial}/${variant.serial}` : variant.serial
                };

                // BY PRIMARY SERIAL
                if (!variantsBySerial[convertedVariant.serial]) {
                    variantsBySerial[convertedVariant.serial] = [];
                }

                variantsBySerial[convertedVariant.serial].push(convertedVariant);
                variantsById[variant.id] = convertedVariant;
                variants.push(convertedVariant);

                // UNIQUE VARIANTS
                // Populate cards with unique images.
                // Default to the non-foil version if there is one.
                const existingUniqueImageVariant: CardVariant | undefined = variantsByImage[variant.imageId];
                if (existingUniqueImageVariant) {
                    if (convertedVariant.finish === "base" && existingUniqueImageVariant.finish !== "base") {
                        variantsByImage[variant.imageId] = convertedVariant;
                    }
                } else {
                    variantsByImage[variant.imageId] = convertedVariant;
                }

                // PLAYABLE VARIANTS
                if (CardFilterCommands.playable.isMatch(convertedVariant)) {
                    if (!playableVariantsBySerial[variant.serial]) {
                        playableVariantsBySerial[variant.serial] = [];
                    }

                    playableVariantsById[variant.id] = convertedVariant;
                    playableVariantsBySerial[variant.serial].push(convertedVariant);
                    playableVariantsList.push(convertedVariant);
                }

                // PROMOS
                if (CardFilterCommands.promo.isMatch(convertedVariant)) {
                    if (!promoVariantsBySerial[variant.serial]) {
                        promoVariantsBySerial[variant.serial] = [];
                    }

                    promoVariantsById[variant.id] = convertedVariant;
                    promoVariantsBySerial[variant.serial].push(convertedVariant);
                    promoVariantsList.push(convertedVariant);
                }

                // FULL ARTS
                if (CardFilterCommands.fullArt.isMatch(convertedVariant)) {
                    if (!fullArtVariantsBySerial[variant.serial]) {
                        fullArtVariantsBySerial[variant.serial] = [];
                    }

                    fullArtVariantsById[variant.id] = convertedVariant;
                    fullArtVariantsBySerial[variant.serial].push(convertedVariant);
                    fullArtVariantsList.push(convertedVariant);
                }

                // PRIMARY VARIANTS
                if (CardFilterCommands.primaryVariant.isMatch(convertedVariant)) {
                    primaryVariantsById[variant.id] = convertedVariant;
                    primaryVariantsBySerial[variant.serial] = convertedVariant;
                    primaryVariantsList.push(convertedVariant);

                    if (CardFilterCommands.playable.isMatch(convertedVariant)) {
                        playablePrimaryVariantsById[variant.id] = convertedVariant;
                        playablePrimaryVariantsBySerial[variant.serial] = convertedVariant;
                        playablePrimaryVariantsList.push(convertedVariant);
                    }
                }
            });

            Object.values(variantsByImage).forEach((variant) => {
                if (!uniqueVariantsBySerial[variant.serial]) {
                    uniqueVariantsBySerial[variant.serial] = [];
                }

                uniqueVariantsById[variant.id] = variant;
                uniqueVariantsBySerial[variant.serial].push(variant);
                uniqueVariantsList.push(variant);

                if (CardFilterCommands.playable.isMatch(variant)) {
                    if (!playableUniqueVariantsBySerial[variant.serial]) {
                        playableUniqueVariantsBySerial[variant.serial] = [];
                    }

                    playableUniqueVariantsById[variant.id] = variant;
                    playableUniqueVariantsBySerial[variant.serial].push(variant);
                    playableUniqueVariantsList.push(variant);
                }
            });

            state.grandfatheredByOpus = getGrandfatheredByOpus(variantsBySerial);
            state.rawCards = action.payload.rawCards;
            state.rawCardVariants = action.payload.rawCardVariants;
            state.jobs = action.payload.jobs;
            state.jobOptions = action.payload.jobs.map(x => ({ label: x, value: x }));
            state.categories = SortCommands.categories.sort(action.payload.categories);
            state.categoryOptions = action.payload.categories.map(x => ({ label: x, value: x }));
            state.artists = action.payload.artists;
            state.artistOptions = action.payload.artists.map(x => ({ label: x, value: x }));

            state.variantCache = {
                any: {
                    all: {
                        list: variants,
                        byId: variantsById,
                        bySerial: expandSerialDictionary(variantsBySerial)
                    },
                    playable: {
                        list: playableVariantsList,
                        byId: playableVariantsById,
                        bySerial: expandSerialDictionary(playableVariantsBySerial)
                    }
                },
                promos: {
                    all: {
                        list: promoVariantsList,
                        byId: promoVariantsById,
                        bySerial: expandSerialDictionary(promoVariantsBySerial)
                    }
                },
                fullArts: {
                    all: {
                        list: fullArtVariantsList,
                        byId: fullArtVariantsById,
                        bySerial: expandSerialDictionary(fullArtVariantsBySerial)
                    }
                },
                primary: {
                    all: {
                        list: primaryVariantsList,
                        byId: primaryVariantsById,
                        bySerial: getPrimaryBy(variantsBySerial, (item) => true)
                    },
                    playable: {
                        list: playablePrimaryVariantsList,
                        byId: playablePrimaryVariantsById,
                        bySerial: getPrimaryBy(variantsBySerial, CardFilterCommands.playable.isMatch)
                    }
                },
                unique: {
                    all: {
                        list: uniqueVariantsList,
                        byId: uniqueVariantsById,
                        bySerial: expandSerialDictionary(uniqueVariantsBySerial)
                    },
                    playable: {
                        list: playableUniqueVariantsList,
                        byId: playableUniqueVariantsById,
                        bySerial: expandSerialDictionary(playableUniqueVariantsBySerial)
                    }
                }
            };
        }
    }
});

function expandSerialDictionary(dictionary: Record<string, CardVariant[]>): Record<string, CardVariant[]> {
    for (const values of Object.values(dictionary)) {
        for (const item of values) {
            if (!item.altSerial) {
                continue;
            }

            if (!dictionary[item.altSerial]) {
                dictionary[item.altSerial] = values;
            }
        }
    }

    return dictionary;
}

function getPrimaryBy(dictionary: Record<string, CardVariant[]>, filter: (item: CardVariant) => boolean): Record<string, CardVariant> {
    const result: Record<string, CardVariant> = {};

    for (const values of Object.values(dictionary)) {
        for (const item of values) {
            const primary = values.find(x => x.isPrimaryVariant);

            if (!primary) {
                continue;
            }

            if (!filter(primary)) {
                continue;
            }

            if (!result[item.serial]) {
                result[item.serial] = primary;
            }

            if (!item.altSerial) {
                continue;
            }

            if (!dictionary[item.altSerial]) {
                result[item.altSerial] = primary;
            }
        }
    }

    return result;

}
function getGrandfatheredByOpus(variantsBySerial: Record<string, CardVariant[]>): Record<number, Record<string, true | undefined> | undefined> {
    const grandfatheredByOpus: Record<number, Record<string, true | undefined> | undefined> = {};
    Object.entries(variantsBySerial).forEach(([serial, variants]) => {
        variants.forEach((variant) => {
            let opusMap = grandfatheredByOpus[variant.opus];
            if (!opusMap) {
                grandfatheredByOpus[variant.opus] = {};
                opusMap = grandfatheredByOpus[variant.opus];
            }

            variants.forEach((innerVariant) => {
                opusMap![innerVariant.id] = true;
            });

            if (variant.altOpus !== undefined) {
                opusMap = grandfatheredByOpus[variant.altOpus];

                if (!opusMap) {
                    grandfatheredByOpus[variant.altOpus] = {};
                    opusMap = grandfatheredByOpus[variant.altOpus];
                }

                variants.forEach((innerVariant) => {
                    opusMap![innerVariant.id] = true;
                });
            }
        });
    });

    return grandfatheredByOpus;
}

const download = (
    url: string,
    setProgress: ActionCreatorWithPayload<number, string>,
    setIsLoading: ActionCreatorWithPayload<boolean, string>,
    setComplete: ActionCreatorWithPayload<boolean, string>,
    dispatch: ThunkDispatch<any, any, any>
) => {
    return axios.get(url, {
        onDownloadProgress: (progressEvent) => {
            dispatch(setProgress(progressEvent.loaded));
        }
    }).then((response) => {
        dispatch(setIsLoading(false));
        dispatch(setComplete(true));
        return response;
    });
}

export const initialize = createAsyncThunk<void, void, { state: RootState }>(
    "app/downloadCards",
    async (_, thunkAPI) => {
        const {
            isJobsLoading,
            isCategoriesLoading,
            isRawCardsLoading,
            isRawCardVariantsLoading,
            isArtistsLoading
        } = thunkAPI.getState().app;

        if (isJobsLoading && isCategoriesLoading && isRawCardsLoading && isRawCardVariantsLoading && isArtistsLoading) {
            return;
        }

        thunkAPI.dispatch(setRawVariantsLoading(true));
        thunkAPI.dispatch(setIsCardsLoading(true));
        thunkAPI.dispatch(setIsJobsLoading(true));
        thunkAPI.dispatch(setIsCategoriesLoading(true));
        thunkAPI.dispatch(setIsArtistsLoading(true));

        Promise.all([
            download(`${FIREBASE_STORAGE_URL}/json/cardsV2.json`, setCardsProgress, setIsCardsLoading, setCardsComplete, thunkAPI.dispatch),
            download(`${FIREBASE_STORAGE_URL}/json/card-variantsV2.json`, setRawVariantsProgress, setRawVariantsLoading, setVariantsComplete, thunkAPI.dispatch),
            download(`${FIREBASE_STORAGE_URL}/json/jobsV2.json`, setJobProgress, setIsJobsLoading, setJobsComplete, thunkAPI.dispatch),
            download(`${FIREBASE_STORAGE_URL}/json/categoriesV2.json`, setCategoryProgress, setIsCategoriesLoading, setCategoriesComplete, thunkAPI.dispatch),
            download(`${FIREBASE_STORAGE_URL}/json/artistsV2.json`, setArtistProgress, setIsArtistsLoading, setArtistsComplete, thunkAPI.dispatch)
        ]).then(([getCardsResponse, getRawCardVariantsResponse, getJobsResponse, getCategoriesResponse, getArtistsResponse]: [AxiosResponse<CardJsonV2[]>, AxiosResponse<VariantJsonV2[]>, AxiosResponse<string[]>, AxiosResponse<string[]>, AxiosResponse<string[]>]) => {
            thunkAPI.dispatch(setRawCards({
                categories: getCategoriesResponse.data,
                rawCardVariants: getRawCardVariantsResponse.data,
                jobs: getJobsResponse.data,
                rawCards: getCardsResponse.data,
                artists: getArtistsResponse.data
            }));
        }).catch(reason => {
            console.log(reason);
        }).finally(() => {
            thunkAPI.dispatch(setIsCardsLoading(false));
            thunkAPI.dispatch(setIsJobsLoading(false));
            thunkAPI.dispatch(setRawVariantsLoading(false));
            thunkAPI.dispatch(setIsCategoriesLoading(false));
            thunkAPI.dispatch(setIsArtistsLoading(false));
        });
    }
)

export const downloadVariantPrices = createAsyncThunk<void, void, { state: RootState }>(
    "app/getVariantPrices",
    async (_, thunkAPI) => {
        const {
            isVariantPriceDictionaryLoading
        } = thunkAPI.getState().app;

        if (isVariantPriceDictionaryLoading) {
            return;
        }

        thunkAPI.dispatch(setIsVariantPriceDictionaryLoading(true));
        axios.get(`${FIREBASE_STORAGE_URL}/json/variant-pricesV2.json`)
            .then((response: AxiosResponse<PriceDictionary>) => {
                thunkAPI.dispatch(setVariantPrices(response.data));
            })
            .catch(() => {
            })
            .finally(() => {
                thunkAPI.dispatch(setIsVariantPriceDictionaryLoading(false));
            });
    }
);

export const downloadFormatRules = createAsyncThunk<void, void, { state: RootState }>(
    "app/getFormatRules",
    async (_, thunkAPI) => {
        try {
            const {
                isFormatRulesLoading
            } = thunkAPI.getState().app;

            if (isFormatRulesLoading) {
                return;
            }

            thunkAPI.dispatch(setIsFormatRulesLoading(true));
            axios.get(`${FIREBASE_STORAGE_URL}/json/format-rules.json`,
                {
                    onDownloadProgress(progressEvent) {
                        thunkAPI.dispatch(setFormatRulesDownloadProgress(progressEvent.progress || 0));

                        if (progressEvent.loaded == 1) {
                            thunkAPI.dispatch(setIsFormatRulesLoading(false));
                        }
                    }
                })
                .then((response: AxiosResponse<FormatRulesDictionary>) => {
                    if (response.status == 200) {
                        thunkAPI.dispatch(setFormatRules(response.data));
                    }
                    else {
                        thunkAPI.dispatch(setIsFormatRulesLoading(false));
                    }
                })
                .catch(reason => {
                    thunkAPI.dispatch(setIsFormatRulesLoading(false));
                });
        } catch {
            thunkAPI.dispatch(setIsFormatRulesLoading(false));
        }
    }
);

export const downloadSiteCost = createAsyncThunk<void, void, { state: RootState }>(
    "app/getSiteCost",
    async (_, thunkAPI) => {
        getSiteCost()
            .then((response) => {
                thunkAPI.dispatch(setSiteCost(response));
            })
            .catch(() => {

            });
    }
);

export const downloadArchetypeNames = createAsyncThunk<void, void, { state: RootState }>(
    "app/downloadArchetypeNames",
    async (_, thunkAPI) => {
        getArchetypeNames()
            .then((response) => {
                thunkAPI.dispatch(setArchetypeNames(response));
            })
            .catch((e) => {

            });
    }
);

export const downloadErrata = createAsyncThunk<void, void, { state: RootState }>(
    "app/downloadErrata",
    async (_, thunkAPI) => {
        try {
            const {
                isErrataLoading
            } = thunkAPI.getState().app;

            if (isErrataLoading) {
                return;
            }

            thunkAPI.dispatch(setIsErrataLoading(true));
            axios.get(`${FIREBASE_STORAGE_URL}/json/errata.json`,
                {
                    onDownloadProgress(progressEvent) {
                        thunkAPI.dispatch(setErrataDownloadProgress(progressEvent.progress || 0));

                        if (progressEvent.loaded == 1) {
                            thunkAPI.dispatch(setIsErrataLoading(false));
                        }
                    }
                })
                .then((response: AxiosResponse<ErrataSerialDictionary>) => {
                    if (response.status == 200) {
                        thunkAPI.dispatch(setErrataSerialDictionary(response.data));
                    }
                    else {
                        thunkAPI.dispatch(setIsErrataLoading(false));
                    }
                })
                .catch(reason => {
                    thunkAPI.dispatch(setIsErrataLoading(false));
                });
        } catch {
            thunkAPI.dispatch(setIsErrataLoading(false));
        }
    }
);

export const downloadFAQ = createAsyncThunk<void, void, { state: RootState }>(
    "app/downloadFAQ",
    async (_, thunkAPI) => {
        try {
            const {
                isFAQLoading
            } = thunkAPI.getState().app;

            if (isFAQLoading) {
                return;
            }

            thunkAPI.dispatch(setIsFAQLoading(true));
            axios.get(`${FIREBASE_STORAGE_URL}/json/faq.json`,
                {
                    onDownloadProgress(progressEvent) {
                        thunkAPI.dispatch(setFAQDownloadProgress(progressEvent.progress || 0));

                        if (progressEvent.loaded == 1) {
                            thunkAPI.dispatch(setIsFAQLoading(false));
                        }
                    }
                })
                .then((response: AxiosResponse<FAQ[]>) => {
                    if (response.status == 200) {
                        thunkAPI.dispatch(setFAQ(response.data));
                    }
                    else {
                        thunkAPI.dispatch(setIsFAQLoading(false));
                    }
                })
                .catch(reason => {
                    thunkAPI.dispatch(setIsFAQLoading(false));
                });
        } catch {
            thunkAPI.dispatch(setIsFAQLoading(false));
        }
    }
);

export const downloadTcgPlayerCardDetailsDictionary = createAsyncThunk<void, void, { state: RootState }>(
    "app/downloadTcgPlayerCardDetails",
    async (_, thunkAPI) => {
        const {
            isTcgPlayerCardDetailsDictionaryLoading
        } = thunkAPI.getState().app;

        if (isTcgPlayerCardDetailsDictionaryLoading) {
            return;
        }

        thunkAPI.dispatch(setIsTcgPlayerCardDetailsDictionaryLoading(true));
        axios.get(`${FIREBASE_STORAGE_URL}/json/tcgplayer-products.json`)
            .then((response: AxiosResponse<TcgPlayerCardDetailsDictionary>) => {
                thunkAPI.dispatch(setTcgPlayerCardDetailsDictionary(response.data));
            })
            .catch(reason => {
            })
            .finally(() => {
                thunkAPI.dispatch(setIsTcgPlayerCardDetailsDictionaryLoading(false));
            });
    }
);

export const downloadProducts = createAsyncThunk<void, void, { state: RootState }>(
    "app/downloadProducts",
    async (_, thunkAPI) => {
        const {
            isProductsLoading
        } = thunkAPI.getState().app;

        if (isProductsLoading) {
            return;
        }

        thunkAPI.dispatch(setIsProductsLoading(true));

        download(`${FIREBASE_STORAGE_URL}/json/productsV2.json`, setProductsProgress, setIsProductsLoading, setProductsComplete, thunkAPI.dispatch)
            .then((response: AxiosResponse<DbProductOverview[]>) => {
                thunkAPI.dispatch(setProducts(response.data));
            }).catch(reason => {
                console.log(reason);
            }).finally(() => {
                thunkAPI.dispatch(setIsProductsLoading(false));
            });
    }
);

export const downloadCollection = createAsyncThunk(
    "collection/download",
    async (_, thunkAPI) => {
        thunkAPI.dispatch(setIsCollectionLoading(true));

        getCardCollection()
            .then(collection => {
                thunkAPI.dispatch(setCollection(collection ?? {}));
            })
            .catch(() => {
                console.warn("Failed to download collection.");
                thunkAPI.dispatch(setCollection({}));
            })
            .finally(() => {
                thunkAPI.dispatch(setIsCollectionLoading(false));
            });
    }
)

export const {
    setCollectionDownloadProgress,
    setIsCollectionLoading,
    setCollection,

    // CATEGORIES
    setIsCategoriesLoading,
    setCategoryProgress,
    setCategoriesComplete,

    // JOBS
    setIsJobsLoading,
    setJobProgress,
    setJobsComplete,

    // ARTIST
    setIsArtistsLoading,
    setArtistProgress,
    setArtistsComplete,

    // CARDS
    setIsCardsLoading,
    setRawCards,
    setCardsProgress,
    setCardsComplete,

    // VARIANTS
    setRawVariantsLoading,
    setRawVariantsProgress,
    setVariantsComplete,

    // PRODUCTS
    setIsProductsLoading,
    setProducts,
    setIsProductDetailsLoading,
    setProductsComplete,
    setProductsProgress,

    // PRICES
    setVariantPriceDownloadProgress,
    setIsVariantPriceDictionaryLoading,
    setVariantPrices,

    setIsFormatRulesLoading,
    setFormatRules,
    setFormatRulesDownloadProgress,

    setIsErrataLoading,
    setErrataDownloadProgress,
    setErrataSerialDictionary,

    setIsFAQLoading,
    setFAQDownloadProgress,
    setFAQ,

    setSiteCost,
    setArchetypeNames,

    setIsTcgPlayerCardDetailsDictionaryLoading,
    setTcgPlayerCardDetailsDictionaryDownloadProgress,
    setTcgPlayerCardDetailsDictionary,

    setLoadingWaitTimerElapsed
} = appSlice.actions;

export default appSlice.reducer;

export type VariantCache = {
    any: {
        all: {
            list: CardVariant[],
            byId: Record<string, CardVariant>,
            bySerial: Record<string, CardVariant[]>
        },
        playable: {
            list: CardVariant[],
            byId: Record<string, CardVariant>,
            bySerial: Record<string, CardVariant[]>
        }
    },
    promos: {
        all: {
            list: CardVariant[],
            byId: Record<string, CardVariant>,
            bySerial: Record<string, CardVariant[]>
        }
    },
    fullArts: {
        all: {
            list: CardVariant[],
            byId: Record<string, CardVariant>,
            bySerial: Record<string, CardVariant[]>
        }
    },
    primary: {
        all: {
            list: CardVariant[],
            byId: Record<string, CardVariant>,
            bySerial: Record<string, CardVariant>
        },
        playable: {
            list: CardVariant[],
            byId: Record<string, CardVariant>,
            bySerial: Record<string, CardVariant>
        }
    },
    unique: {
        all: {
            list: CardVariant[],
            byId: Record<string, CardVariant>,
            bySerial: Record<string, CardVariant[]>
        },
        playable: {
            list: CardVariant[],
            byId: Record<string, CardVariant>,
            bySerial: Record<string, CardVariant[]>
        }
    }
}

export type AppState = {
    dirtyVariants: Record<string, number>,

    isCollectionLoading: boolean,
    collectionDownloadProgress: number,
    collection: Record<string, number>,

    isCategoriesLoading: boolean,
    categories: string[],
    categoriesComplete: boolean,
    categoryOptions: ComboBoxOption<string>[],
    categoryProgress: number,

    isJobsLoading: boolean,
    jobsComplete: boolean,
    jobs: string[],
    jobOptions: ComboBoxOption<string>[],
    jobProgress: number,

    isArtistsLoading: boolean,
    artists: string[],
    artistsComplete: boolean,
    artistOptions: ComboBoxOption<string>[],
    artistProgress: number,

    isRawCardsLoading: boolean,
    isRawCardsInitialized: boolean,
    cardsComplete: boolean,
    rawCards: CardJsonV2[],
    rawCardsProgress: number,

    isRawCardVariantsLoading: boolean,
    isRawCardVariantsInitialized: boolean,
    variantsComplete: boolean,
    variantsProgress: number,

    variantCache: VariantCache,
    rawCardVariants: VariantJsonV2[],
    grandfatheredByOpus: Record<number, Record<string, true | undefined> | undefined>,

    isProductsLoading: boolean,
    isProductsInitialized: boolean,
    products: DbProductOverview[],
    productIdDictionary: Record<number, DbProductOverview>,
    isProductDetailsLoading: boolean,
    productsProgress: number,
    productsComplete: boolean,

    siteCost: SiteCost,
    archetypeNames: ArchetypeName[],

    isVariantPriceDictionaryLoading: boolean,
    isVariantPriceDictionaryInitialized: boolean,
    variantPriceDictionary: PriceDictionary,
    variantPriceDownloadProgress: number,

    isTcgPlayerCardDetailsDictionaryLoading: boolean,
    tcgPlayerCardDetailsDictionary: TcgPlayerCardDetailsDictionary,
    tcgPlayerCardDetailsDictionaryDownloadProgress: number,

    isFormatRulesLoading: boolean,
    formatRulesDownloadProgress: number,
    formatRules: FormatRulesDictionary,

    isErrataLoading: boolean,
    errataDownloadProgress: number,
    errataSerialDictionary: ErrataSerialDictionary,

    isFAQLoading: boolean,
    faqDownloadProgress: number,
    faq: FAQ[],

    loadingWaitTimerElapsed: boolean
}

export function getDefaultVariantCache(): VariantCache {
    return {
        any: {
            all: {
                list: [],
                byId: {},
                bySerial: {}
            },
            playable: {
                list: [],
                byId: {},
                bySerial: {}
            }
        },
        promos: {
            all: {
                list: [],
                byId: {},
                bySerial: {}
            }
        },
        fullArts: {
            all: {
                list: [],
                byId: {},
                bySerial: {}
            }
        },
        primary: {
            all: {
                list: [],
                byId: {},
                bySerial: {}
            },
            playable: {
                list: [],
                byId: {},
                bySerial: {}
            }
        },
        unique: {
            all: {
                list: [],
                byId: {},
                bySerial: {}
            },
            playable: {
                list: [],
                byId: {},
                bySerial: {}
            }
        }
    };
}

function getInitialState(): AppState {
    return {
        dirtyVariants: {},

        isCollectionLoading: false,
        collectionDownloadProgress: 0,
        collection: {},

        // CATEGORIES
        isCategoriesLoading: false,
        categories: [],
        categoriesComplete: false,
        categoryOptions: [],
        categoryProgress: 0,

        // JOBS
        isJobsLoading: false,
        jobs: [],
        jobsComplete: false,
        jobOptions: [],
        jobProgress: 0,

        // ARTISTS
        isArtistsLoading: false,
        artists: [],
        artistsComplete: false,
        artistOptions: [],
        artistProgress: 0,

        // CARDS
        isRawCardsLoading: false,
        isRawCardsInitialized: false,
        rawCards: [],
        cardsComplete: false,
        rawCardsProgress: 0,

        // VARIANTS
        isRawCardVariantsLoading: false,
        isRawCardVariantsInitialized: false,
        variantsProgress: 0,
        variantsComplete: false,
        rawCardVariants: [],
        grandfatheredByOpus: {},

        variantCache: getDefaultVariantCache(),
        siteCost: {
            createdDate: 0,
            debuff: 0,
            level: 0,
            month: 0,
            progress: 0,
            time: 0,
            year: 0
        },

        archetypeNames: [],

        isProductsLoading: false,
        isProductsInitialized: false,
        products: [],
        productIdDictionary: {},
        isProductDetailsLoading: false,
        productsComplete: false,
        productsProgress: 0,

        isVariantPriceDictionaryLoading: false,
        isVariantPriceDictionaryInitialized: false,
        variantPriceDictionary: {},
        variantPriceDownloadProgress: 0,

        isTcgPlayerCardDetailsDictionaryLoading: false,
        tcgPlayerCardDetailsDictionary: {},
        tcgPlayerCardDetailsDictionaryDownloadProgress: 0,

        formatRulesDownloadProgress: 0,
        isFormatRulesLoading: false,
        formatRules: {
            L3: {
                name: "",
                rules: [],
                allowed: {
                    cards: []
                },
                banned: {
                    cards: []
                }
            },
            L6: {
                name: "",
                rules: [],
                allowed: {
                    cards: []
                },
                banned: {
                    cards: []
                }
            },
            F5: {
                name: "",
                rules: [],
                allowed: {
                    cards: []
                },
                banned: {
                    cards: []
                }
            },
            Standard: {
                name: "",
                rules: [],
                allowed: {
                    cards: []
                },
                banned: {
                    cards: []
                }
            },
            Title: {
                name: "",
                rules: [],
                allowed: {
                    cards: []
                },
                banned: {
                    cards: []
                }
            },
            Sealed: {
                name: "",
                rules: [],
                allowed: {
                    cards: []
                },
                banned: {
                    cards: []
                }
            },
            Draft: {
                name: "",
                rules: [],
                allowed: {
                    cards: []
                },
                banned: {
                    cards: []
                }
            },
            AllStarDraft: {
                name: "",
                rules: [],
                allowed: {
                    cards: []
                },
                banned: {
                    cards: []
                }
            },
            JudgeTower: {
                name: "",
                rules: [],
                allowed: {
                    cards: []
                },
                banned: {
                    cards: []
                }
            },
            Pauper: {
                name: "",
                rules: [],
                allowed: {
                    cards: []
                },
                banned: {
                    cards: []
                }
            }
        },

        isErrataLoading: false,
        errataDownloadProgress: 0,
        errataSerialDictionary: {},

        isFAQLoading: false,
        faqDownloadProgress: 0,
        faq: [],

        loadingWaitTimerElapsed: false
    }
}